import { router, useLocalSearchParams } from 'expo-router';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useCreateNpoPaymentAccountSteps, useSaveNpoPaymentAccountSteps } from '@/api/hooks/paymentAccountsHooks';
import { Bank, NpoPaymentAccountStepsResponse } from '@/api/models/paymentAccountsModels';
import FormWrapper from '@/components/forms/FormWrapper';
import BankAccountForm from '@/components/forms/paymentNpoOnboarding/BankAccountForm';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useNpoMerchantOnboardingState } from '@/contexts/npoMerchantOnboardingContext';
import { BankAccountData } from '@/lib/validations/NpoPaymentOnboarding';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

const BankAccount = () => {
  const { npoItem } = useLocalSearchParams<{
    npoItem: string;
  }>();

  const {
    state: merchantState,
    setNpoId,
    setInitialNpoMerchantData,
    completeMerchantOnboarding
  } = useNpoMerchantOnboardingState();
  const merchantStateResponse = safeJsonParse<NpoPaymentAccountStepsResponse>(
    merchantState.initialNpoMerchantData ?? ''
  );

  const { mutateAsync: saveStep, isPending } = useCreateNpoPaymentAccountSteps();
  const { mutateAsync: createAccount, isPending: isPendingCreateAccount } = useSaveNpoPaymentAccountSteps();

  const { showSuccess } = useEndFlowModal();

  const bankAccountData = merchantStateResponse?.result?.bankData;
  const initialData: BankAccountData = {
    bankRoutingNumber: bankAccountData?.bank_routing_number ?? '',
    bankAccountNumber: bankAccountData?.bank_routing_number ?? ''
  };

  const parseBankAccountData = (data: BankAccountData): Bank => {
    const parsedBankAccount = {
      bank_routing_number: data.bankRoutingNumber,
      bank_account_number: data.bankAccountNumber
    };

    return parsedBankAccount;
  };

  return (
    <SafeAreaView style={style.flexOne} edges={['bottom']}>
      <FormWrapper scrollEnable minHeightMinus={180}>
        <BankAccountForm
          initialData={initialData}
          isLoading={isPending || isPendingCreateAccount}
          onDone={(data) => {
            const parsedBankAccount = parseBankAccountData(data);

            saveStep(
              {
                type: 'both',
                body: {
                  nonProfitOrgId: merchantState.npoId ?? '',
                  bank: parsedBankAccount
                }
              },
              {
                onSuccess: (data) => {
                  setNpoId(data.nonProfitOrgId);
                  setInitialNpoMerchantData(
                    JSON.stringify({
                      files: [],
                      result: data
                    })
                  );
                  completeMerchantOnboarding();
                  if (data.allStepsSaved) {
                    createAccount(
                      {
                        nonProfitOrgId: merchantState.npoId ?? ''
                      },
                      {
                        onSuccess: (_) => {
                          showSuccess({
                            title: i18n.t('profile.merchant_created_account'),
                            message: i18n.t('profile.need_documents_message'),
                            closeOnTapOutside: false,
                            showCloseButton: false,
                            buttonLabel: i18n.t('profile.go_to_documents'),
                            onButtonPress: () => {
                              router.dismissAll();
                              router.push('/(app)/(bottomTabs)/profile/npoProfile/insights');
                              router.navigate({
                                pathname: '/npoPaymentOnboarding/documents',
                                params: {
                                  npoId: merchantState.npoId ?? '',
                                  npoItem
                                }
                              });
                            }
                          });
                        }
                      }
                    );
                  }
                }
              }
            );
          }}
          onSaveAndContinue={(data) => {
            const parsedBankAccount = parseBankAccountData(data);

            saveStep(
              {
                type: 'both',
                body: {
                  nonProfitOrgId: merchantState.npoId ?? '',
                  bank: parsedBankAccount
                }
              },
              {
                onSuccess: (data) => {
                  setNpoId(data.nonProfitOrgId);
                  setInitialNpoMerchantData(JSON.stringify(data));
                  completeMerchantOnboarding();
                  showSuccess({
                    title: i18n.t('profile.saved_continue_title'),
                    message: i18n.t('profile.saved_continue_description'),
                    onButtonPress: () => {
                      router.dismissAll();
                      router.push('/(app)/(bottomTabs)/profile/npoProfile/insights');
                    }
                  });
                }
              }
            );
          }}
        />
      </FormWrapper>
    </SafeAreaView>
  );
};

export default BankAccount;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  }
});
